import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import './SandboxBrowser.css';

const DEFAULT_HOME = 'https://www.google.com';
const OPERATOR_FEATURES_OPEN_KEY = 'zolaid-browser-operator-features-open';

/** Same-origin document URL. The dev server fetches the page and strips framing blocks. */
function computerPreviewSrc(pageUrl, cacheBust) {
  const u = String(pageUrl || '').trim();
  if (!u || u.startsWith('about:')) return u || 'about:blank';
  const base = `/computer-preview?url=${encodeURIComponent(u)}`;
  return cacheBust ? `${base}&_cb=${cacheBust}` : base;
}

const SEARCH_MODES = [
  { id: 'web', label: 'Web' },
  { id: 'images', label: 'Images' },
  { id: 'videos', label: 'Videos' },
  { id: 'shopping', label: 'Shopping' },
];

function ensureHttpUrl(input) {
  const text = String(input || '').trim();
  if (!text) return '';
  if (/^https?:\/\//i.test(text)) {
    return text.replace(/^https?:\/\//i, (scheme) => scheme.toLowerCase());
  }
  if (text.startsWith('//')) return `https:${text}`;
  return `https://${text.replace(/^\/+/, '')}`;
}

function normalizeUrl(input, searchMode = 'web') {
  const u = (input || '').trim();
  if (!u) return DEFAULT_HOME;
  if (/^https?:\/\//i.test(u) || u.startsWith('//')) return ensureHttpUrl(u);
  // If it looks like a URL (has a dot, no space), add https
  if (u.includes('.') && !u.includes(' ')) return ensureHttpUrl(u);
  // Otherwise treat as search query \u2013 use Google Web / Images / Videos / Shopping
  const tbm = searchMode === 'images' ? 'isch' : searchMode === 'videos' ? 'vid' : searchMode === 'shopping' ? 'shop' : '';
  const base = 'https://www.google.com/search?q=' + encodeURIComponent(u);
  return tbm ? `${base}&tbm=${tbm}` : base;
}

const OPERATOR_TAKEOVER_KEY = 'zolaid-operator-takeover';
const OPERATOR_CONFIRM_KEY = 'zolaid-operator-confirm';
const OPERATOR_STEALTH_KEY = 'zolaid-operator-stealth';

/**
 * SandboxBrowser - Sandboxed browser iframe for navigating external URLs.
 * Operator capabilities (takeover, confirmation, stealth) can be controlled via props or internal toggles.
 * Ref API: navigateTo(url), getCurrentUrl(), getFrameRect() for parent-controlled navigation and cursor overlay.
 */
const SandboxBrowser = forwardRef(function SandboxBrowser({
  url = '',
  onClose,
  takeoverMode: takeoverModeProp,
  onTakeoverModeChange,
  confirmationRequired: confirmationRequiredProp,
  onConfirmationRequiredChange,
  stealthMode: stealthModeProp,
  onStealthModeChange,
  onUrlChange,
  /** When true, render inside the shared Preview panel (no Operator settings chrome). */
  embedded = false,
  /** When true, the parent Computer header owns back, forward, reload, and the address bar. */
  hideControls = false,
}, ref) {
  const iframeRef = useRef(null);
  const frameContainerRef = useRef(null);
  const [searchMode, setSearchMode] = useState('web');
  const [currentUrl, setCurrentUrl] = useState(() => normalizeUrl(url, 'web'));
  const [inputUrl, setInputUrl] = useState(() => normalizeUrl(url, 'web'));
  const [isLoading, setIsLoading] = useState(true);
  const [liveNote, setLiveNote] = useState('');
  const [frameBlocked, setFrameBlocked] = useState(false);
  const [blockMessage, setBlockMessage] = useState('');
  const [operatorFeaturesOpen, setOperatorFeaturesOpen] = useState(() => {
    try {
      return localStorage.getItem(OPERATOR_FEATURES_OPEN_KEY) === 'true';
    } catch (_err) {
      return false;
    }
  });

  const [localTakeover, setLocalTakeover] = useState(() => {
    try {
      return localStorage.getItem(OPERATOR_TAKEOVER_KEY) !== 'false';
    } catch (_err) {
      return true;
    }
  });
  const [localConfirmation, setLocalConfirmation] = useState(() => {
    try {
      return localStorage.getItem(OPERATOR_CONFIRM_KEY) !== 'false';
    } catch (_err) {
      return true;
    }
  });
  const [localStealth, setLocalStealth] = useState(() => {
    try {
      return localStorage.getItem(OPERATOR_STEALTH_KEY) === 'true';
    } catch (_err) {
      return false;
    }
  });

  const takeoverMode = takeoverModeProp !== undefined ? takeoverModeProp : localTakeover;
  const confirmationRequired = confirmationRequiredProp !== undefined ? confirmationRequiredProp : localConfirmation;
  const stealthMode = stealthModeProp !== undefined ? stealthModeProp : localStealth;

  const setTakeover = (v) => {
    setLocalTakeover(v);
    try {
      localStorage.setItem(OPERATOR_TAKEOVER_KEY, String(v));
    } catch (_) {}
    onTakeoverModeChange?.(v);
  };
  const setConfirmation = (v) => {
    setLocalConfirmation(v);
    try {
      localStorage.setItem(OPERATOR_CONFIRM_KEY, String(v));
    } catch (_) {}
    onConfirmationRequiredChange?.(v);
  };
  const setStealth = (v) => {
    setLocalStealth(v);
    try {
      localStorage.setItem(OPERATOR_STEALTH_KEY, String(v));
    } catch (_) {}
    onStealthModeChange?.(v);
  };

  const toggleOperatorFeatures = () => {
    const next = !operatorFeaturesOpen;
    setOperatorFeaturesOpen(next);
    try {
      localStorage.setItem(OPERATOR_FEATURES_OPEN_KEY, String(next));
    } catch (_) {}
  };

  // Expose navigation and frame info to parent (e.g. AIBrowserAgent) so it can control the sandbox
  useImperativeHandle(ref, () => ({
    navigateTo: (u) => {
      const final = ensureHttpUrl(u);
      if (final) {
        setFrameBlocked(false);
        setBlockMessage('');
        setLiveNote('');
        setCurrentUrl(final);
        setInputUrl(final);
        setIsLoading(true);
      }
    },
    back: () => {
      try { iframeRef.current?.contentWindow?.history.back(); } catch (_) {}
    },
    forward: () => {
      try { iframeRef.current?.contentWindow?.history.forward(); } catch (_) {}
    },
    reload: () => {
      const frame = iframeRef.current;
      if (!frame) return;
      setIsLoading(true);
      try {
        frame.contentWindow.location.reload();
      } catch (_) {
        frame.src = computerPreviewSrc(currentUrl, Date.now());
      }
    },
    getCurrentUrl: () => currentUrl,
    getFrameRect: () => frameContainerRef.current?.getBoundingClientRect() ?? null,
  }), [currentUrl]);

  // Sync with url prop when it changes (e.g. from chatbot) \u2013 opens website by itself
  useEffect(() => {
    const u = (url || '').trim();
    if (!u) return;
    const withProtocol = ensureHttpUrl(u);
    setFrameBlocked(false);
    setBlockMessage('');
    setLiveNote('');
    setCurrentUrl(withProtocol);
    setInputUrl(withProtocol);
    setIsLoading(true);
    if (typeof onUrlChange === 'function') onUrlChange(withProtocol);
  }, [url, onUrlChange]);

  const navigateTo = (finalUrl) => {
    const u = ensureHttpUrl(finalUrl);
    if (u) {
      setFrameBlocked(false);
      setBlockMessage('');
      setLiveNote('');
      setCurrentUrl(u);
      setInputUrl(u);
      setIsLoading(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNavigate();
    }
    if (e.key === 'Escape') {
      e.target.blur();
      if (onClose) onClose();
    }
  };

  useEffect(() => {
    if (!isLoading) return undefined;
    const timer = setTimeout(() => {
      setIsLoading(false);
      setFrameBlocked(true);
      setBlockMessage('This page took too long to load.');
    }, 35000);
    return () => clearTimeout(timer);
  }, [isLoading, currentUrl]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== 'zolaid-live-browser') return;
      if (data.url) {
        setInputUrl(data.url);
        if (typeof onUrlChange === 'function') onUrlChange(data.url);
      }
      setLiveNote(data.note || '');
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onUrlChange]);

  useEffect(() => {
    if (!isLoading) return undefined;
    const timer = setInterval(() => {
      try {
        const doc = iframeRef.current?.contentDocument;
        if (!doc || doc.readyState === 'loading') return;
        const blocked = doc.documentElement?.getAttribute('data-preview-error') === '1';
        const note = doc.documentElement?.getAttribute('data-preview-note') || '';
        if (blocked) {
          setFrameBlocked(true);
          setBlockMessage((doc.body?.innerText || '').replace(/\s+/g, ' ').trim() || 'This page could not be loaded.');
        }
        setLiveNote(note);
        setIsLoading(false);
      } catch (_) {}
    }, 250);
    return () => clearInterval(timer);
  }, [isLoading, currentUrl]);

  const handleLoad = () => {
    setIsLoading(false);
    const frame = iframeRef.current;
    let shown = '';
    let blocked = false;
    let message = '';
    let note = '';
    try {
      const root = frame?.contentDocument?.documentElement;
      shown = root?.getAttribute('data-preview-url') || '';
      blocked = root?.getAttribute('data-preview-error') === '1';
      note = root?.getAttribute('data-preview-note') || '';
      message = (frame?.contentDocument?.body?.innerText || '').replace(/\s+/g, ' ').trim();
    } catch (_) {
      shown = '';
      blocked = false;
      note = '';
    }
    setFrameBlocked(blocked);
    setBlockMessage(blocked ? (message || 'This page could not be loaded.') : '');
    setLiveNote(blocked ? '' : note);
    if (shown && typeof onUrlChange === 'function') onUrlChange(shown);
  };

  const handleRefresh = () => {
    const frame = iframeRef.current;
    if (!frame) return;
    setIsLoading(true);
    try {
      frame.contentWindow.location.reload();
    } catch (_) {
      frame.src = computerPreviewSrc(currentUrl, Date.now());
    }
  };

  const handleBack = () => {
    if (iframeRef.current?.contentWindow) {
      try {
        iframeRef.current.contentWindow.history.back();
      } catch (err) {
        console.warn('SandboxBrowser: back not available', err);
      }
    }
  };

  const handleForward = () => {
    if (iframeRef.current?.contentWindow) {
      try {
        iframeRef.current.contentWindow.history.forward();
      } catch (err) {
        console.warn('SandboxBrowser: forward not available', err);
      }
    }
  };

  const goHome = () => {
    navigateTo(DEFAULT_HOME);
  };

  const handleNavigate = () => {
    const u = (inputUrl || '').trim();
    if (!u) {
      goHome();
      return;
    }
    navigateTo(normalizeUrl(u, searchMode));
  };

  return (
    <div
      className={`sandbox-browser${embedded ? ' sandbox-browser--embedded' : ''}`}
      role="application"
      aria-label="Sandboxed web browser"
    >
      {!hideControls && <div className="browser-controls">
        <div className="browser-controls-row browser-controls-top">
          {!embedded && (
            <div className="browser-search-modes" aria-label="Search type">
              {SEARCH_MODES.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  className={`browser-btn search-mode-btn ${searchMode === id ? 'active' : ''}`}
                  onClick={() => setSearchMode(id)}
                  title={`Search ${label}`}
                  aria-label={label}
                  aria-pressed={searchMode === id}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className="browser-url-bar">
            <span className="sandbox-badge" title={"Isolated sandbox \u2013 safe preview"}>{'\uD83D\uDD12'}</span>
            <input
              type="url"
              className="url-input"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search or enter URL"
              aria-label="Address bar"
              spellCheck={false}
              autoComplete="off"
            />
            <button type="button" className="browser-btn" onClick={handleBack} title="Back" aria-label="Back">
              {'\u2190'}
            </button>
            <button type="button" className="browser-btn" onClick={handleForward} title="Forward" aria-label="Forward">
              {'\u2192'}
            </button>
            <button type="button" className="browser-btn" onClick={handleRefresh} title="Reload page" aria-label="Reload page">
              {'\u27F3'}
            </button>
            <button
              type="button"
              className="browser-btn go-btn"
              onClick={handleNavigate}
              aria-label="Go"
            >
              Go
            </button>
            <a
              href={currentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="browser-btn open-external-btn"
              title={"Open in new tab (if site won\u2019t load here)"}
              aria-label="Open in new tab"
            >
              {'\u2197'}
            </a>
          </div>
          {onClose && !embedded && (
            <button
              type="button"
              className="browser-btn close-btn"
              onClick={onClose}
              title="Close (Esc)"
              aria-label="Close browser"
            >
              {'\u2715'}
            </button>
          )}
        </div>
      </div>}

      {/* Operator settings stay outside Preview \u2014 capabilities are toggled from chat + menu */}
      {!embedded && (
      <div className="operator-features-wrap">
        <button
          type="button"
          className="operator-features-toggle"
          onClick={toggleOperatorFeatures}
          aria-expanded={operatorFeaturesOpen}
        >
          {operatorFeaturesOpen ? 'â–¼' : 'â–¶'} Operator settings
        </button>
        {operatorFeaturesOpen && (
          <div className="operator-features-content">
            <section className="operator-capability-toggle">
              <div className="operator-capability-head">
                <h6>Takeover mode</h6>
                <button
                  type="button"
                  className={`operator-toggle-btn ${takeoverMode ? 'on' : 'off'}`}
                  onClick={() => setTakeover(!takeoverMode)}
                  aria-pressed={takeoverMode}
                  title={takeoverMode ? 'On: you enter passwords/payment' : 'Off'}
                >
                  <span className="operator-toggle-knob" />
                  <span className="operator-toggle-label">{takeoverMode ? 'ON' : 'OFF'}</span>
                </button>
              </div>
              <p className="operator-capability-desc">{'Sensitive fields (passwords, payment) \u2192 you type the value'}</p>
            </section>

            <section className="operator-capability-toggle">
              <div className="operator-capability-head">
                <h6>Action confirmation</h6>
                <button
                  type="button"
                  className={`operator-toggle-btn ${confirmationRequired ? 'on' : 'off'}`}
                  onClick={() => setConfirmation(!confirmationRequired)}
                  aria-pressed={confirmationRequired}
                  title={confirmationRequired ? 'On: confirm before high-risk actions' : 'Off'}
                >
                  <span className="operator-toggle-knob" />
                  <span className="operator-toggle-label">{confirmationRequired ? 'ON' : 'OFF'}</span>
                </button>
              </div>
              <p className="operator-capability-desc">Confirm before purchases, sends, or deletes</p>
            </section>

            <section className="operator-capability-toggle">
              <div className="operator-capability-head">
                <h6>Watch / Stealth</h6>
                <button
                  type="button"
                  className={`operator-toggle-btn ${stealthMode ? 'on' : 'off'}`}
                  onClick={() => setStealth(!stealthMode)}
                  aria-pressed={stealthMode}
                  title={stealthMode ? 'stealth' : 'watch'}
                >
                  <span className="operator-toggle-knob" />
                  <span className="operator-toggle-label">{stealthMode ? 'STEALTH' : 'WATCH'}</span>
                </button>
              </div>
              <p className="operator-capability-desc">Watch = visible automation; Stealth = minimal footprint</p>
            </section>

            <section className="operator-capability-info">
              <h6>Agent capabilities</h6>
              <ul className="operator-capability-list">
                <li>Chain-of-thought, self-correction, vision &amp; coordinates</li>
                <li>Browser automation, code execution, file create/upload</li>
                <li>{'Isolated sandbox \u2014 safe preview; open in new tab to use your cookies'}</li>
              </ul>
            </section>
          </div>
        )}
      </div>
      )}

      <div className="browser-frame-container" ref={frameContainerRef}>
        {isLoading ? (
          <div className="browser-loading-overlay" role="status" aria-live="polite">
        {liveNote ? (
          <div className="browser-live-note" role="status">
            <p>{liveNote}</p>
            {currentUrl ? (
              <a href={currentUrl} target="_blank" rel="noopener noreferrer">Open this page</a>
            ) : null}
          </div>
        ) : null}
            <span className="browser-loading-spinner" aria-hidden="true" />
            <span className="browser-loading-text">{'Loading page\u2026'}</span>
          </div>
        ) : null}
        {frameBlocked ? (
          <div className="browser-blocked-card" role="alert">
            <p>{blockMessage || 'This page could not be loaded.'}</p>
            {currentUrl ? (
              <a
                className="browser-open-new-tab-link"
                href={currentUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open this page
              </a>
            ) : null}
          </div>
        ) : null}
        <iframe
          ref={iframeRef}
          src={computerPreviewSrc(currentUrl)}
          className="browser-iframe"
          title="Preview browser"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          onLoad={handleLoad}
        />
        {currentUrl ? (
          <a
            href={currentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="browser-open-new-tab-corner"
            title="Open in new tab"
          >
            {'Open in new tab \u2197'}
          </a>
        ) : null}
      </div>
    </div>
  );
});
SandboxBrowser.displayName = 'SandboxBrowser';
export default SandboxBrowser;
